import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Link } from 'foundations-components/transitional/components/Link';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { getMaxAdditionalUnits, getQuantityPackSteppingFunction } from 'pricing-pages-lib/utils/contactsUtils';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import { useEffect, useId, useState } from 'react';
import { MARKETING } from 'self-service-api/constants/ProductLines';
import { CALCULATOR, MARKETING_PLUS_CARD } from '../../../constants/tracking/TrackingSources';
import { useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { useAppSelector } from '../../../reducers';
import { minPurchasableContactsSelector } from '../../../selectors/ContactsSelectors';
import { calculatedPriceForProductConfigurationsInListSelector } from '../../../selectors/PricingSelectors';
import { hasHigherProductLevelInProductInventorySelector, hasProductInProductInventorySelector, productsFromProductInventoryQuantityPackQuantitySelector } from '../../../selectors/ProductInventorySelectors';
import { productSelector, skuSelector } from '../../../selectors/ProductsSelectors';
import { usePricingLibSelector } from '../../../selectors/SelectorUtils';
import { trackAdditionalUnitsPicker, trackModalInteraction } from '../../../utils/tracking/trackingEventUtils';
import AsyncContactsPriceBreakdownModal from '../pricing/AsyncContactsPriceBreakdownModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ContactsPickerCompactAll = ({
  quantityPackSkuId,
  quantityPackQuantity,
  onChange,
  productConfigurations,
  trackingSource = CALCULATOR,
  queryParamTerm,
  isLoadingQuantityPacks
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const isMarketingPlusCard = trackingSource === MARKETING_PLUS_CARD;
  const [priceBreakdownVisible, setPriceBreakdownVisible] = useState(false);
  const quantityPack = useAppSelector(state => skuSelector(state, {
    skuId: quantityPackSkuId
  }));
  const {
    includedWithQuantityPack,
    includedWithProductVersion,
    productId,
    quantityPackApiName
  } = quantityPack;
  const {
    apiName,
    productLine,
    productLevel
  } = useAppSelector(state => productSelector(state, {
    productId
  }));
  const minPurchasableContacts = useAppSelector(state => minPurchasableContactsSelector(state, {
    quantityPackSkuId
  })) || 0;
  const ownedQuantityPackQuantity = useAppSelector(state => productsFromProductInventoryQuantityPackQuantitySelector(state, {
    productIdentifier: {
      apiName
    },
    quantityPackApiName
  }));
  const ownsProduct = useAppSelector(state => hasProductInProductInventorySelector(state, {
    productAttributeFilters: {
      productLine,
      productLevel
    }
  }));
  const ownedUnitQuantity = ownsProduct ? includedWithProductVersion + ownedQuantityPackQuantity : 0;
  const ownsHigherProductLevel = useAppSelector(state => hasHigherProductLevelInProductInventorySelector(state, {
    productLine: MARKETING,
    productLevel
  }));
  const currencyCode = useDisplayedCurrencyCode();
  const maxAdditionalUnits = getMaxAdditionalUnits(quantityPack);
  const maxContactsInPlan = maxAdditionalUnits + includedWithProductVersion;
  const steppingFunction = getQuantityPackSteppingFunction(quantityPack);
  useEffect(() => {
    if (quantityPackQuantity < minPurchasableContacts && !ownsHigherProductLevel) {
      onChange(steppingFunction(minPurchasableContacts));
    } else if (steppingFunction(quantityPackQuantity) !== quantityPackQuantity) {
      onChange(steppingFunction(quantityPackQuantity));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantityPackQuantity, minPurchasableContacts]);
  useEffect(() => {
    if (priceBreakdownVisible) {
      trackModalInteraction({
        action: 'click to open',
        name: 'contact-picker-more-details-modal',
        trackingSource,
        productLine,
        productLevel
      });
    }
  }, [priceBreakdownVisible, productLevel, productLine, trackingSource]);
  const startingPriceForContacts = useAppSelector(state => calculatedPriceForProductConfigurationsInListSelector(state, {
    allProductConfigurations: productConfigurations,
    productConfigurations: [createProductConfig({
      skuId: quantityPackSkuId
    })],
    queryParamTerm,
    displayedCurrencyCode: currencyCode
  })).monthly.calculatedPrice;
  const minUnitsToDisplay = ownsHigherProductLevel ? includedWithProductVersion : includedWithProductVersion + minPurchasableContacts;
  const handleContactsChange = (newValue, roundUp) => {
    const additionalUnitQuantity = steppingFunction(newValue - includedWithProductVersion, roundUp);
    trackAdditionalUnitsPicker({
      action: `stepper ${additionalUnitQuantity + includedWithProductVersion > steppingFunction(quantityPackQuantity) + includedWithProductVersion ? 'increase' : 'decrease'}`,
      trackingSource,
      productLine,
      productLevel,
      ownsProduct,
      skuId: quantityPackSkuId,
      ownedUnitQuantity,
      displayedUnitQuantity: includedWithProductVersion + additionalUnitQuantity
    });
    onChange(additionalUnitQuantity);
  };
  const getMarketingContactsMessageLinkElement = () => {
    return {
      Link: ({
        children
      }) => /*#__PURE__*/_jsx(View, {
        marginBlockStart: "200",
        display: "inline-block",
        as: "span",
        children: /*#__PURE__*/_jsx(Link, {
          onClick: () => setPriceBreakdownVisible(true),
          onMouseEnter: () => void AsyncContactsPriceBreakdownModal.preload(),
          "data-selenium-id": "marketing-contacts-modal-open-button",
          children: children
        })
      })
    };
  };
  const marketingContactsTitleId = useId();
  const marketingContactsDescriptionId = useId();
  const includedMarketingContactsId = useId();
  const additionalMarketingContactsId = useId();
  const stepperAriaDescribedBy = [marketingContactsTitleId, marketingContactsDescriptionId, includedMarketingContactsId, additionalMarketingContactsId].join(' ');
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [priceBreakdownVisible && /*#__PURE__*/_jsx(AsyncContactsPriceBreakdownModal, {
      quantityPackSkuId: quantityPackSkuId,
      handleModalClose: () => setPriceBreakdownVisible(false),
      queryParamTerm: queryParamTerm
    }), /*#__PURE__*/_jsx(View, {
      marginBlockStart: "200",
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        alignItems: "start",
        gap: "200",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "heading-50",
          id: marketingContactsTitleId,
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "pricingPage.contactsPicker.marketingContacts.title_jsx",
            elements: Object.assign({
              IncludedAdditionalUnits: () => /*#__PURE__*/_jsx(FormattedNumber, {
                value: includedWithProductVersion
              })
            }, isSeatsRedesignUngated && getMarketingContactsMessageLinkElement())
          })
        }), /*#__PURE__*/_jsxs(Flex, {
          justifyContent: "start",
          children: [!isSeatsRedesignUngated && /*#__PURE__*/_jsx(Text, {
            as: "span",
            typography: "detail-100",
            id: marketingContactsDescriptionId,
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "pricingPage.contactsPicker.marketingContacts.description_jsx",
              elements: Object.assign({
                QuantityMultiplier: () => /*#__PURE__*/_jsx(FormattedNumber, {
                  value: includedWithQuantityPack
                }),
                Price: () => /*#__PURE__*/_jsx(PriceDisplay, {
                  price: startingPriceForContacts,
                  currencyCode: currencyCode
                })
              }, !isSeatsRedesignUngated && getMarketingContactsMessageLinkElement())
            })
          }), !(isSeatsRedesignUngated || isMarketingPlusCard) && /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(StepperInput, {
              "aria-label": I18n.text('pricingPage.contactsPicker.a11y.selectMarketingContacts'),
              "aria-describedby": stepperAriaDescribedBy,
              stepSize: includedWithQuantityPack,
              value: steppingFunction(quantityPackQuantity) + includedWithProductVersion,
              min: minUnitsToDisplay,
              max: maxContactsInPlan,
              onChange: event => handleContactsChange(parseInt(event.target.value, 10), true),
              disabled: isLoadingQuantityPacks,
              "data-selenium-id": "contacts-picker-input"
            })
          })]
        })]
      })
    })]
  });
};
export default ContactsPickerCompactAll;